(() => {
if (customElements.get('cart-drawer')) return;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !this.classList.contains('active')) return;
      const dialog = this.querySelector('.drawer__inner');
      const focusable = Array.from(dialog.querySelectorAll('a[href], button:enabled, input:not([type="hidden"]):enabled, textarea:enabled, select:enabled, summary, [tabindex="0"], iframe, shop-pay-wallet-button, shopify-amazon-pay-button, shopify-apple-pay-button, shopify-google-pay-button, shopify-paypal-button'))
        .filter((element) => element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first) return;
      event.stopPropagation();
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        const nativeWallet = last.matches?.('shop-pay-wallet-button, shopify-amazon-pay-button, shopify-apple-pay-button, shopify-google-pay-button, shopify-paypal-button');
        const focusEnd = nativeWallet ? dialog.querySelector('[data-clashi-cart-focus-end]') : null;
        if (nativeWallet && focusEnd) {
          focusEnd.focus({ preventScroll: true });
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        event.stopPropagation();
        first.focus();
      }
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
      const packsLink = event.target.closest('[data-clashi-cart-packs]');
      const browseLink = event.target.closest('[data-clashi-cart-browse]');
      const packsSection = getPacksSection();
      if (packsLink && packsSection && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        this.close();
        focusPacksSection(packsSection);
      } else if (browseLink && new URL(browseLink.href).pathname === window.location.pathname && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button === 0) {
        event.preventDefault();
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    this.cleanupFocus?.();
    this.openTimer = setTimeout(() => {
      this.classList.add('animate', 'active');
      const dialog = this.querySelector('.drawer__inner');
      let focused = false;
      const focusDialog = () => {
        if (focused || !this.classList.contains('active')) return;
        focused = true;
        clearTimeout(this.focusTimer);
        dialog.removeEventListener('transitionend', onTransition);
        trapFocus(this.querySelector('.drawer__inner-empty') || dialog, this.querySelector('.drawer__close') || dialog);
      };
      const onTransition = (event) => {
        if (event.target === dialog && event.propertyName === 'transform') focusDialog();
      };
      this.cleanupFocus = () => dialog.removeEventListener('transitionend', onTransition);
      dialog.addEventListener('transitionend', onTransition);
      this.focusTimer = setTimeout(focusDialog, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300);
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    this.cleanupFocus?.();
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.classList.toggle('is-empty', this.querySelector('cart-drawer-items').classList.contains('is-empty'));

    const self = this;
    (window.requestAnimationFrame || function (fn) { setTimeout(fn, 0); })(function () {
      self.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

// A deferred script in a custom layout can load the drawer before cart.js.
customElements.whenDefined('cart-items').then(() => {
if (customElements.get('cart-drawer-items')) return;
class CartDrawerItems extends customElements.get('cart-items') {
  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (['cart-items', 'product-form', 'cart-upsell', 'atc-upsell'].includes(event.source)) return;
      this.onCartUpdate();
    });
  }

  onCartUpdate() {
    return fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' })
      .then((response) => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.text();
      })
      .then((responseText) => {
        if (!this.isConnected) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const source = html.querySelector('#CartDrawer .drawer__inner');
        const drawer = document.querySelector('cart-drawer');
        const target = drawer?.querySelector('.drawer__inner');
        if (!source || !target) return;
        const focusedId = target.contains(document.activeElement) ? document.activeElement.id : null;
        target.innerHTML = source.innerHTML;
        drawer.classList.toggle('is-empty', target.querySelector('cart-drawer-items').classList.contains('is-empty'));
        if (drawer.classList.contains('active')) {
          trapFocus(target, (focusedId && document.getElementById(focusedId)) || target.querySelector('.drawer__close'));
        }
        document.dispatchEvent(new CustomEvent('clashi-cart:rendered'));
      })
      .catch(() => {
        if (!this.isConnected) return;
        const error = document.getElementById('CartDrawer-CartErrors');
        if (error) error.textContent = window.cartStrings.error;
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
});

function getPacksSection() {
  return document.querySelector('.vfx-catalog-section-label--professional') || document.querySelector('[data-i18n="catalog_zone_professional_packs"]');
}

function focusPacksSection(section) {
  section.id = section.id || 'packs-profesionales';
  section.setAttribute('tabindex', '-1');
  section.style.scrollMarginTop = '110px';
  section.focus({ preventScroll: true });
  section.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
}

function preparePacksSection() {
  const section = getPacksSection();
  if (section && !document.getElementById('packs-profesionales')) section.id = 'packs-profesionales';
  if (section && window.location.hash === '#packs-profesionales') focusPacksSection(section);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', preparePacksSection, { once: true });
else preparePacksSection();
})();
